import React, { useState, useRef, useEffect } from 'react';
import classnames from 'classnames';
import styles from './tabs.module.scss';

export type TabsProps = {
  items: {
    headline: string;
    content: JSX.Element;
    id: string;
    disabled?: boolean;
    onClick?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  }[];
  initialTab?: number;
  size?: '200' | '300';
  stretchButtons?: boolean;
  className?: string;
  manualActivation?: boolean;
};

export const Tabs: React.FC<TabsProps> = ({
  items,
  className,
  initialTab,
  size = '200',
  stretchButtons = false,
  manualActivation = false
}) => {
  const [isActive, setIsActive] = useState(initialTab || 0);
  const [focusIndex, setFocusIndex] = useState(initialTab || 0);
  const [hasKeyboardInteraction, setHasKeyboardInteraction] = useState(false);
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);

  // Focus the tab button when focusIndex changes and keyboard interaction has occurred
  useEffect(() => {
    if (hasKeyboardInteraction) {
      const tabButton = tabRefs.current[focusIndex];
      if (tabButton) {
        tabButton.focus();
      }
    }
  }, [focusIndex, hasKeyboardInteraction]);

  // Helper function to find the next enabled tab
  const findNextEnabledTab = (currentIndex: number): number => {
    let nextIndex = currentIndex;
    let attempts = 0;

    while (attempts < items.length) {
      nextIndex = (nextIndex + 1) % items.length;
      if (!items[nextIndex].disabled) {
        return nextIndex;
      }
      attempts++;
    }

    return currentIndex;
  };

  // Helper function to find the previous enabled tab
  const findPreviousEnabledTab = (currentIndex: number): number => {
    let prevIndex = currentIndex;
    let attempts = 0;
    while (attempts < items.length) {
      prevIndex = (prevIndex - 1 + items.length) % items.length;
      if (!items[prevIndex].disabled) {
        return prevIndex;
      }
      attempts++;
    }
    return currentIndex;
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    const target = event.target as HTMLButtonElement;
    if (target.ariaDisabled === 'true') {
      event.preventDefault();
      return;
    }

    // Set keyboard interaction flag on first keyboard event
    if (!hasKeyboardInteraction) {
      setHasKeyboardInteraction(true);
    }

    let newFocusIndex = focusIndex;
    let shouldActivate = false;

    switch (event.key) {
      case 'ArrowLeft':
        newFocusIndex = findPreviousEnabledTab(focusIndex);
        shouldActivate = !manualActivation;
        break;
      case 'ArrowRight':
        newFocusIndex = findNextEnabledTab(focusIndex);
        shouldActivate = !manualActivation;
        break;
      case 'Home':
        newFocusIndex = findNextEnabledTab(-1);
        shouldActivate = !manualActivation;
        break;
      case 'End':
        newFocusIndex = findPreviousEnabledTab(0);
        shouldActivate = !manualActivation;
        break;
      case 'Enter':
      case ' ':
        if (manualActivation && !items[focusIndex].disabled) {
          shouldActivate = true;
        }
        break;
      default:
        return;
    }

    event.preventDefault();
    setFocusIndex(newFocusIndex);
    if (shouldActivate) {
      setIsActive(newFocusIndex);
    }
  };

  return (
    <div className={classnames(styles.tabs, className, styles[`s${size}`])} data-testid="tabs-component">
      <div role="tablist" className={classnames(styles.tablist, { [styles.stretch]: stretchButtons })}>
        {items.map((item, i) => {
          const buttonClass = classnames(styles.button, {
            [styles.active]: i === isActive
          });

          return (
            <button
              ref={(el) => (tabRefs.current[i] = el)}
              key={`${item.headline}-${i}-control`}
              id={`${item.id}-control`}
              aria-controls={`${item.id}-panel`}
              aria-selected={i === isActive}
              className={buttonClass}
              role="tab"
              type="button"
              onClick={(e) => {
                if (!item.disabled) {
                  setIsActive(i);
                  setFocusIndex(i);
                  item.onClick && item.onClick(e);
                }
              }}
              onKeyDown={handleKeyDown}
              disabled={item.disabled}
              aria-disabled={item.disabled}
              tabIndex={i === isActive ? 0 : -1}
            >
              {item.headline}
            </button>
          );
        })}
      </div>
      {items.map((item, i) => {
        const panelClass = classnames(styles.panel, {
          [styles.active]: i === isActive
        });

        return (
          <div
            aria-hidden={i !== isActive}
            key={`${item.headline}-${i}-panel`}
            id={`${item.id}-panel`}
            role="tabpanel"
            aria-labelledby={`${item.id}-control`}
            className={panelClass}
            tabIndex={0}
          >
            {item.content}
          </div>
        );
      })}
    </div>
  );
};
